// SystemSetting.jsx - システム設定画面
// 管理項目: list_sending_time（一覧送付時間） / printing_company_email（送信用印刷会社メールアドレス）
// 依存: axios, @tanstack/react-query
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import './systemsetting.css';
import useNotificationStore from '../stores/notificationStore';

/* ========================================
 * 定数
 * ======================================== */
const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * ISO 文字列 / "HH:mm:ss" / "HH:mm" → "HH:mm"（time input 用）
 */
const toTimeInput = (val) => {
  if (!val) return '';
  // "HH:mm:ss" or "HH:mm" 形式はそのままスライス
  if (/^\d{2}:\d{2}/.test(val)) return val.slice(0, 5);
  // ISO 文字列の場合は Date 経由で取り出す
  const d = new Date(val);
  if (isNaN(d)) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/* ========================================
 * メイン：システム設定フォーム
 * ======================================== */
export default function SystemSetting() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationStore();

  /* ===== 現在設定取得 ===== */
  const {
    data: settingData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['system-setting'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/admin/system-setting/`);
      if (!res.data?.success) throw new Error('設定の取得に失敗しました');
      return res.data.setting || {};
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  /* ===== フォーム State ===== */
  const [formData, setFormData] = useState({
    list_sending_time: '',
    printing_company_email: '',
  });

  const [errors, setErrors] = useState({});

  // 取得データをフォームに反映
  useEffect(() => {
    if (!settingData) return;
    setFormData({
      list_sending_time: toTimeInput(settingData.list_sending_time),
      printing_company_email: settingData.printing_company_email || '',
    });
    setErrors({});
  }, [settingData]);

  /* ===== 更新 Mutation ===== */
  const updateMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.patch(`${API_URL}/api/admin/system-setting/update/`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || '更新に失敗しました');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-setting'] });
      showNotification('システム設定を更新しました');
    },
    onError: (err) => {
      const message = err.response?.data?.message || err.message || '更新に失敗しました';
      window.alert(message);
    },
  });

  /* ===== ハンドラ ===== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};

    // メールアドレスは入力がある場合のみ形式チェック
    if (
      formData.printing_company_email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.printing_company_email.trim())
    ) {
      newErrors.printing_company_email = '有効なメールアドレスを入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      list_sending_time: formData.list_sending_time || null,
      printing_company_email: formData.printing_company_email.trim() || null,
    };

    updateMutation.mutate(payload);
  };

  const handleReset = () => {
    if (!settingData) return;
    setFormData({
      list_sending_time: toTimeInput(settingData.list_sending_time),
      printing_company_email: settingData.printing_company_email || '',
    });
    setErrors({});
  };

  const isSaving = updateMutation.isPending;

  return (
    <div className="ssPageRoot">
      <h2 className="h01-page-title">システム設定</h2>

      <section className="h01-section">
        {isError && (
          <div className="ssErrorAlert">{error?.message || '設定の取得に失敗しました'}</div>
        )}
        {isLoading && <div className="ssEmpty">読み込み中...</div>}

        {!isLoading && !isError && (
          <form onSubmit={handleSubmit} className="ssFormBody">

            {/* ===== 一覧送付設定 ===== */}
            <div className="ssFormSection">
              <h3 className="ssFormSectionTitle">一覧送付設定</h3>

              <div className="ssFormGrid">
                <div className="ssFormGroup ssFormGroup-full">
                  <label className="ssFormLabel">一覧送付時間</label>
                  <div className="ssFieldHint">
                    出陳犬一覧を自動送付する時間を設定します。未設定の場合は自動送付されません。
                  </div>

                  <div className="ssDatetimeWrapper">
                    <input
                      type="time"
                      name="list_sending_time"
                      className={`ssFormInput ssTimeInput ${errors.list_sending_time ? 'ss-is-error' : ''}`}
                      value={formData.list_sending_time}
                      onChange={handleChange}
                    />
                    {formData.list_sending_time && (
                      <button
                        type="button"
                        className="ssClearBtn"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, list_sending_time: '' }))
                        }
                        title="クリア"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {errors.list_sending_time && (
                    <span className="ssErrorText">{errors.list_sending_time}</span>
                  )}
                </div>
              </div>
            </div>

            {/* ===== 印刷会社設定 ===== */}
            <div className="ssFormSection">
              <h3 className="ssFormSectionTitle">印刷会社設定</h3>

              <div className="ssFormGrid">
                <div className="ssFormGroup ssFormGroup-full">
                  <label className="ssFormLabel">送信用印刷会社メールアドレス</label>
                  <div className="ssFieldHint">
                    印刷会社へのデータ送信先メールアドレスを設定します。最大 320 文字。
                  </div>

                  <input
                    type="email"
                    name="printing_company_email"
                    maxLength={320}
                    className={`ssFormInput ${errors.printing_company_email ? 'ss-is-error' : ''}`}
                    value={formData.printing_company_email}
                    onChange={handleChange}
                    placeholder="example@printcompany.co.jp"
                  />

                  {errors.printing_company_email && (
                    <span className="ssErrorText">{errors.printing_company_email}</span>
                  )}
                </div>
              </div>
            </div>

            {/* ===== フッター ===== */}
            <div className="ssFormFooter">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
                disabled={isSaving}
              >
                リセット
              </button>

              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                {isSaving ? '保存中...' : '設定を保存する'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}